import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Menu,
  MenuItem,
  Popover,
  Typography,
  useMediaQuery,
} from "@mui/material";
import BasicInfoCardQuickActionsStyles from "./BasicInfoCardQuickActionsStyles";
import ContactButtonComponent from "../QuickActions/ContactButtonComponent";
import ContactComponentStyles from "../QuickActions/ContactComponentStyles";
import phone from "../../../../assets/phone.svg";
import AutoPaymentComponent from "../QuickActions/AutoPaymentComponent";
import FileAClaim from "../../../../components/FileAClaim/FileAClaim";
import { useLocation, useParams } from "react-router-dom";
import { IDownloadIdCardParams } from "../../../../models/user/dashboard/Dashboard";
import { GetDownloadIdCard } from "../../../Dashboard/DashboardAction";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { getCookie, isFeatureAvailable } from "../../../../utils/Utility";
import { IPolicyVehicle } from "../../../../models/user/policyDetails/PolicyDetails";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import { featuresCodes } from "../../../../constants/Constants";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import PolicyChange from "../../../Dashboard/Policies/PolicyChange/PolicyChange";
import { resetCommonSlice } from "../../../../store/common/CommonSlice";
import CloseIcon from "@mui/icons-material/Close";
import { GetOpenTasks } from "./BasicInfoCardActions";

interface IProps {
  handlePayment: () => void;
  carrierPolicyNumber: string;
  policyVehicles: IPolicyVehicle[];
}

export interface ITaksParams {
  providerId: string;
  policyId: string;
}

const BasicInfoCardQuickActions: React.FC<IProps> = ({
  handlePayment,
  carrierPolicyNumber,
  policyVehicles,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1200px)");
  const roleCode = getCookie("roleCode");
  const id = getCookie("id");
  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { policyId, lobCode } = useParams();

  const { addTaskResponse } = useAppSelector((state) => state.addTask);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  const { features } = useAppSelector((state) => state.features);
  const { openTasksData } = useAppSelector((state) => state.openTaks);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [fileAClaimOpen, setFileAClaimOpen] = useState<boolean>(false);
  const [changePolicyOpen, setChangePolicyOpen] = useState<boolean>(false);
  const [policyChangeDrawerTitle, setPolicyChangeDrawerTitle] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const resetPolicyChangeRef = useRef<(() => void) | null>(null);

  const getFeatureCodes = (featureCode: string) => {
    // console.log(featureCode);
    if (!featureCode || !lobCode) {
      return;
    }
    const name: any =
      featuresCodes[featureCode as keyof typeof getFeatureCodes];
    return name[lobCode];
  };

  const buttons = useMemo(() => {
    return [
      "File A Claim",
      "Agent Details",
      "Pay",
      "Download ID",
      "Roadside Assistance",
      "Change Policy",
    ]
      .filter((button) => {
        switch (button) {
          case "Pay":
            return isFeatureAvailable({
              featureCodes: getFeatureCodes(button),
              features,
            });
          case "Download ID":
            return (
              lobCode &&
              lobCode === "PA" &&
              isFeatureAvailable({
                featureCodes: getFeatureCodes(button),
                features,
              })
            );
          case "File A Claim":
            return isFeatureAvailable({
              featureCodes: getFeatureCodes(button),
              features,
            });
          case "Roadside Assistance":
            return (
              lobCode &&
              lobCode === "PA" &&
              isFeatureAvailable({
                featureCodes: getFeatureCodes(button),
                features,
              })
            );
          case "Change Policy":
            return isFeatureAvailable({
              featureCodes: getFeatureCodes("Change Policy Single Screen"),
              features,
            });
          default:
            return true;
        }
      })
      .slice(0, isMobile ? 1 : 6);
  }, [features, isMobile, lobCode]);

  const filterMoreOptions = useCallback(
    (options: string[]): string[] => {
      return options
        .filter((option) => {
          switch (option) {
            case "Cancel Policy":
              return isFeatureAvailable({
                featureCodes: getFeatureCodes(option),
                features,
              });
            case "Download ID":
              return (
                lobCode &&
                lobCode === "PA" &&
                isFeatureAvailable({
                  featureCodes: getFeatureCodes(option),
                  features,
                })
              );
            case "Pay":
              return isFeatureAvailable({
                featureCodes: getFeatureCodes(option),
                features,
              });
            case "Roadside Assistance":
              return (
                lobCode &&
                lobCode === "PA" &&
                isFeatureAvailable({
                  featureCodes: getFeatureCodes(option),
                  features,
                })
              );
            case "Change Policy":
              return isFeatureAvailable({
                featureCodes: getFeatureCodes("Change Policy Single Screen"),
                features,
              });
            default:
              return true;
          }
        })
        .slice(
          isFeatureAvailable({
            featureCodes: getFeatureCodes("File A Claim"),
            features,
          })
            ? 0
            : 1,
          options?.length
        );
    },
    [features, lobCode]
  );

  const moreOptions = useMemo(
    (): string[] =>
      filterMoreOptions([
        "Agent Details",
        "Pay",
        "Download ID",
        "Roadside Assistance",
        "Change Policy",
      ]),
    [filterMoreOptions]
  );

  const open = Boolean(anchorEl);

  const handleClick = async ({
    event,
    text,
  }: {
    event?: React.MouseEvent<HTMLButtonElement>;
    text: string;
  }) => {
    setSelectedAction(text);
    if (event) {
      setAnchorEl(event.currentTarget);
    }
    if (text === "File A Claim") {
      setFileAClaimOpen(true);
    }
    if (text === "Download ID") {
      handleDownloadIdCardClick(event);
    }
    if (text === "Pay") {
      handlePayment();
    }
    if (text === "Change Policy") {
      setPolicyChangeDrawerTitle("");
      setChangePolicyOpen(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedAction("");
  };

  const renderPopoverContent = () => {
    switch (selectedAction) {
      case "File A Claim":
        return (
          <FileAClaim
            open={fileAClaimOpen}
            setOpen={setFileAClaimOpen}
            policyId={policyId || ""}
            lobCode={lobCode || ""}
            carrier={carrier || ""}
            system={system || ""}
          />
        );
      case "Agent Details":
        return (
          <Popover
            sx={BasicInfoCardQuickActionsStyles.contactPop}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            disableRestoreFocus
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
             transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <ContactButtonComponent />
          </Popover>
        );
        case "Roadside Assistance":
        return (
          <Popover
            sx={BasicInfoCardQuickActionsStyles.contactPop}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            disableRestoreFocus
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Box sx={ContactComponentStyles.contactContainer}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Typography variant="body1" sx={ContactComponentStyles.heading}>
                  {t("Roadside Assistance")}
                </Typography>
                <Box sx={ContactComponentStyles.contactContactNumber}>
                  <Box sx={ContactComponentStyles.contactPhoneLogo}>
                    {<img src={phone} alt="Phone" />}
                  </Box>
                  <Typography sx={ContactComponentStyles.contactNumber}>
                    1-800-555-0000
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Popover>
        );
      case "Download ID":
        return (
          <Popover
            sx={BasicInfoCardQuickActionsStyles.contactPop}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            disableRestoreFocus
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{ p: 2, minWidth: 250, maxWidth: 350 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "600",
                "@media(max-width:780px)":{fontSize: "14px"},
                "@media(min-width:781px)":{fontSize: "16px"} }}>
                {t("Select Vehicle")}
              </Typography>
              {policyVehicles.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, textAlign: "center" }}>
                  {t("No vehicles found")}
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {policyVehicles.map((vehicle, index) => (
                    <ListItem
                      key={vehicle.vin || index}
                      button
                      onClick={() => handleVehicleSelect(vehicle)}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <Typography variant="body1"sx={{ fontWeight: "400",
                        "@media(max-width:780px)":{fontSize: "14px"}, fontSize: "16px"}}>
                        {vehicle.make} {vehicle.model}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Popover>
        );
      case "Enable Auto Pay":
        return <AutoPaymentComponent onClose={handleClose} open={open} />;
      case "More":
        return (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {moreOptions.map((option) => (
              <MenuItem
                key={option}
                onClick={(event) => handleClick({ text: option })}
                sx={{
                  color: "#1D252B",
                  fontFamily: "Noto Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                }}
              >
                {t(option)}
              </MenuItem>
            ))}
          </Menu>
        );
      case "Change Policy":
        return (
          <CustomDrawer
            open={changePolicyOpen}
            onClose={closePolicyChangeDrawer}
            title={
              policyChangeDrawerTitle
                ? t(policyChangeDrawerTitle)
                : `${t("Change Policy")} ${carrierPolicyNumber}`
            }
            content={
              <PolicyChange
                setPolicyChangeDrawerTitle={setPolicyChangeDrawerTitle}
                lobCode={lobCode || ""}
                policyId={policyId || ""}
                closePolicyChangeDrawer={closePolicyChangeDrawer}
                setChangePolicyOpen={setChangePolicyOpen}
                resetPolicyChangeState={resetPolicyChangeRef}
              />
            }
            onBack={policyChangeDrawerTitle ? handleBackToPolicyChange : undefined}
            // headerStyles={{
            //   "& .MuiTypography-root": {
            //     fontFamily: "Noto Sans !important",
            //     fontWeight: "bold",
            //   },
            // }}
            contentStyles={{
              padding:
                policyChangeDrawerTitle && policyChangeDrawerTitle?.length
                  ? 0
                  : "1.25rem 1.5rem",
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleDownloadIdCardClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!policyId || !lobCode) {
      return;
    }
    
    if (event) {
      setAnchorEl(event.currentTarget);
    }
    setSelectedAction("Download ID");
    
    // If only one vehicle, download directly
    if (policyVehicles.length === 1) {
      handleDownloadIdCard();
      handleClose();
    }
    // If multiple vehicles or no vehicles, popover will show the list or message
  };

  const handleVehicleSelect = (vehicle: IPolicyVehicle) => {
    handleDownloadIdCard();
    handleClose();
  };

  const handleDownloadIdCard = () => {
    if (!policyId || !lobCode) {
      return;
    }
    const requestParams: IDownloadIdCardParams = {
      policyId,
      lobCode,
    };
    dispatch(GetDownloadIdCard(requestParams));
  };

  useEffect(() => {
    if (isMobile || isTablet) {
      setAnchorEl(null);
      setSelectedAction("");
    }
  }, [isMobile, isTablet]);

  const closePolicyChangeDrawer = () => {
    dispatch(resetCommonSlice());
    setChangePolicyOpen(false);
    setPolicyChangeDrawerTitle("");
  };

  const handleBackToPolicyChange = () => {
    setPolicyChangeDrawerTitle("");
    if (resetPolicyChangeRef.current) {
      resetPolicyChangeRef.current();
    }
  };

  useEffect(() => {
    const pathArray = location.pathname.split("/");
    if (pathArray[1] !== "agent") return;
    if (policyId?.trim() && id.trim()) {
      const requestParams: ITaksParams = {
        providerId: id,
        policyId,
      };
      dispatch(GetOpenTasks(requestParams));
    }
  }, [dispatch, addTaskResponse]);
  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const isTaskOverdue = (dueDateStr: string) => {
    if (!dueDateStr) return;
    const today = new Date();
    const dueDate = new Date(dueDateStr);

    // Reset time part for accurate date comparison
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Function to get chip color based on priority
  const getPriorityChipStyle = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      case "medium":
        return { bgcolor: "#fff8e1", color: "#ff8f00" };
      case "low":
        return { bgcolor: "#e8f5e9", color: "#388e3c" };
      default:
        return { bgcolor: "#e0e0e0", color: "#616161" };
    }
  };

  const handleOpenTasksClick = () => {
    if (openTasksData && openTasksData?.taskCount > 0) {
      toggleDrawer();
    }
  };
  const handleTaskClick = (id: string) => {
    const pathArray = location.pathname.split("/");
    if (pathArray[1] !== "agent") return;
    if (!id) return;
    window.location.href = `/agent/mytasks?id=${id}`;
  };
  return (
    <>
      {roleCode === "Producer" ||
      roleCode === "Agent" ||
      roleCode === "Agency" ||
      roleCode === "Group" ? (
        <Box 
          id="quickactions_container"
          data-test-id="test-quickactions_container"
          aria-label="Quick Actions Container"
          sx={BasicInfoCardQuickActionsStyles.BasicInfoQuickActionContainer}
        >
          <Grid 
            id="quickactions_grid"
            data-test-id="test-quickactions_grid"
            aria-label="Quick Actions Grid"
            item 
            xs={12} 
            md={1}
          >
            <Box 
              id="quickactions_tasks"
              data-test-id="test-quickactions_tasks"
              aria-label="Quick Actions Tasks"
              sx={BasicInfoCardQuickActionsStyles.taskContainer}
            >
              <Link
                id="quickactions_tasks-link"
                data-test-id="test-quickactions_tasks-link"
                aria-label="Open Tasks Link"
                component="button"
                variant="subtitle1"
                onClick={handleOpenTasksClick}
                disabled={openTasksData?.taskCount === 0}
                sx={{
                  ...BasicInfoCardQuickActionsStyles.openTasksLink,
                  opacity: openTasksData?.taskCount ? 1 : 0.5,
                  cursor: openTasksData?.taskCount ? "pointer" : "default",
                }}
              >
                Open Tasks
              </Link>
              <Typography
                id="quickactions_tasks-count"
                data-test-id="test-quickactions_tasks-count"
                aria-label="Tasks Count"
                variant="h4"
                sx={BasicInfoCardQuickActionsStyles.taskCountText}
              >
                {openTasksData?.taskCount ?? 0}
              </Typography>
            </Box>
          </Grid>
          <Drawer 
            id="quickactions_tasks-drawer"
            data-test-id="test-quickactions_tasks-drawer"
            aria-label="Tasks Drawer"
            anchor="right" 
            open={openDrawer} 
            onClose={toggleDrawer}
          >
            <Box 
              id="quickactions_tasks-drawer-content"
              data-test-id="test-quickactions_tasks-drawer-content"
              aria-label="Tasks Drawer Content"
              sx={BasicInfoCardQuickActionsStyles.drawerContainer}
            >
              <Box 
                id="quickactions_tasks-drawer-header"
                data-test-id="test-quickactions_tasks-drawer-header"
                aria-label="Tasks Drawer Header"
                sx={BasicInfoCardQuickActionsStyles.drawerHeader}
              >
                <Typography
                  id="quickactions_tasks-drawer-title"
                  data-test-id="test-quickactions_tasks-drawer-title"
                  aria-label="Tasks Drawer Title"
                  variant="h6"
                  sx={BasicInfoCardQuickActionsStyles.titleText}
                >
                  Open Tasks
                </Typography>
                <IconButton 
                  id="quickactions_tasks-drawer-close"
                  data-test-id="test-quickactions_tasks-drawer-close"
                  aria-label="Close Tasks Drawer"
                  onClick={toggleDrawer}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <List 
                id="quickactions_tasks-list"
                data-test-id="test-quickactions_tasks-list"
                aria-label="Tasks List"
                sx={BasicInfoCardQuickActionsStyles.taskList}
              >
                {openTasksData?.agentTasks.map((task) => {
                  const overdue = isTaskOverdue(task.dueDate);
                  return (
                    <ListItem
                      id={`quickactions_task-item-${task.id}`}
                      data-test-id={`test-quickactions_task-item-${task.id}`}
                      aria-label={`Task Item ${task.id}`}
                      key={task.id}
                      sx={BasicInfoCardQuickActionsStyles.taskItem}
                    >
                      <Box 
                        id={`quickactions_task-header-${task.id}`}
                        data-test-id={`test-quickactions_task-header-${task.id}`}
                        aria-label={`Task Header ${task.id}`}
                        sx={BasicInfoCardQuickActionsStyles.taskHeader}
                      >
                        <Link
                          id={`quickactions_task-link-${task.id}`}
                          data-test-id={`test-quickactions_task-link-${task.id}`}
                          aria-label={`Task Link ${task.id}`}
                          component="button"
                          variant="subtitle1"
                          underline="hover"
                          sx={BasicInfoCardQuickActionsStyles.taskLink}
                          onClick={() => handleTaskClick(task.id)}
                        >
                          {task.taskName}
                        </Link>
                        <Chip
                          id={`quickactions_task-priority-${task.id}`}
                          data-test-id={`test-quickactions_task-priority-${task.id}`}
                          aria-label={`Task Priority ${task.id}`}
                          label={
                            task.priority.charAt(0).toUpperCase() +
                            task.priority.slice(1)
                          }
                          size="small"
                          sx={getPriorityChipStyle(task.priority)}
                        />
                      </Box>
                      <Typography
                        id={`quickactions_task-due-date-${task.id}`}
                        data-test-id={`test-quickactions_task-due-date-${task.id}`}
                        aria-label={`Task Due Date ${task.id}`}
                        variant="body2"
                        sx={
                          overdue
                            ? BasicInfoCardQuickActionsStyles.overdueText
                            : BasicInfoCardQuickActionsStyles.normalText
                        }
                      >
                        Due: {formatDate(task.dueDate)}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </Box>
      ) : (
        <Box 
          id="quickactions_container"
          data-test-id="test-quickactions_container"
          aria-label="Quick Actions Container"
          sx={BasicInfoCardQuickActionsStyles.BasicInfoQuickActionContainer}
        >
          {isMobile ? (
            <>
              {buttons.map((text, index) => (
                <Button
                  id={`quickactions_button-${text}`}
                  data-test-id={`test-quickactions_button-${text}`}
                  aria-label={`${text} Button`}
                  variant="outlined"
                  key={`${text}_${index}`}
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => handleClick({ event, text })}
                  sx={{ width: "max-content" }}
                >
                  {t(text)}
                </Button>
              ))}
              <IconButton
                id="quickactions_more-button"
                data-test-id="test-quickactions_more-button"
                aria-label="More Actions Button"
                onClick={(event) => handleClick({ event, text: "More" })}
              >
                <MoreVertIcon sx={{ fontSize: "2rem" }} />
              </IconButton>
              {renderPopoverContent()}
            </>
          ) : (
            <>
              {isTablet ? (
                <>
                  {buttons.map((text, index) => (
                    <Button
                      id={`quickactions_button-${text}`}
                      data-test-id={`test-quickactions_button-${text}`}
                      aria-label={`${text} Button`}
                      variant="outlined"
                      sx={{
                        width: "max-content",
                        ":disabled": {
                          backgroundColor: "white !important",
                          color: "black",
                        },
                      }}
                      key={`${text}_${index}`}
                      onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ) => handleClick({ event, text })}
                    >
                      {t(text)}
                    </Button>
                  ))}
                  {renderPopoverContent()}
                </>
              ) : (
                <>
                  <Typography 
                    id="quickactions_title"
                    data-test-id="test-quickactions_title"
                    aria-label="Quick Actions Title"
                    sx={BasicInfoCardQuickActionsStyles.quickStyle}
                  >
                    {t("Quick Actions")}
                  </Typography>
                  <Box
                    id="quickactions_buttons-container"
                    data-test-id="test-quickactions_buttons-container"
                    aria-label="Quick Actions Buttons Container"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                    }}
                  >
                    {buttons.map((text, index) => (
                      <Button
                        id={`quickactions_button-${text}`}
                        data-test-id={`test-quickactions_button-${text}`}
                        aria-label={`${text} Button`}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          ":disabled": {
                            backgroundColor: "white !important",
                            color: "black",
                          },
                        }}
                        key={`${text}_${index}`}
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                        ) => handleClick({ event, text })}
                      >
                        {t(text)}
                        
                      </Button>
                    ))}
                  </Box>
                  {renderPopoverContent()}
                </>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default BasicInfoCardQuickActions;
